/**
 * The pictures of the inspection report: a defect's photo, and the few small
 * pictures beside each row of its tables — a surface's or a fixture's shots,
 * a device's circled crop — that a reader opens to see which one is meant.
 *
 * A shot as stored is drawn on for the model and carries its caption (seconds,
 * distances, depth readings), never a homeowner's. The depth service also
 * writes each one outlined without it (`thumbPath`); a run older than that has
 * none, and only the review page shows the stored shot in its place. A device
 * is cut from the unmarked recording like a defect's photo, so needs neither.
 */

/** At most this many pictures beside one row. */
const VIEWS_PER_ROW = 3;

/** The lists of a report's room whose rows carry pictures. */
const ROW_LISTS = ['surfaceRows', 'items', 'devices'];

/**
 * A finding's or a device's evidence picture, or null: the shot as stored
 * (drawn on for the model), its box, and where that frame sits in the room's
 * walkthrough recording — `videoSec` and the recording's key `roomUuid` — so
 * the report can show the unmarked frame instead. Either is null when the doc
 * does not say. `mark`: anything carrying `evidence: { shot, box }`.
 */
function evidencePhoto(doc, mark) {
  const ev = (mark && mark.evidence) || {};
  const shots =
    doc.evidence && Array.isArray(doc.evidence.shots) ? doc.evidence.shots : [];
  const shot = Number.isInteger(ev.shot) ? shots[ev.shot] : null;
  if (!shot || !shot.path || !Array.isArray(ev.box) || ev.box.length !== 4)
    return null;
  return {
    path: shot.path,
    box: ev.box.map(Number),
    videoSec: Number.isFinite(shot.videoSec) ? shot.videoSec : null,
    roomUuid: doc.roomUuid || null,
  };
}

/**
 * How well a shot pictures its item: the share of the item it holds
 * (`framedShare`) times the share of the picture the item fills
 * (`frameCover`), both the depth service's. Either alone misleads: no shot
 * holds more than a few percent of a floor, so the first ranks a sliver at a
 * doorway's foot above a view of the whole floor; a close-up of one cabinet
 * door fills the picture, so the second ranks it above the whole cabinet.
 * Null when the shot does not say.
 */
function pictureScore(shot) {
  return Number.isFinite(shot.framedShare) && Number.isFinite(shot.frameCover)
    ? shot.framedShare * shot.frameCover
    : null;
}

/**
 * The shots of `docs` — a surface's, or every box of a run of cabinets — that
 * picture it best (`pictureScore`), `VIEWS_PER_ROW` at most. A shot that does
 * not say how it frames goes after those that do. → [{ key, path, thumbPath }],
 * `key` naming the picture in the share's uploads.
 */
function shotViews(docs) {
  const all = [];
  docs.forEach((doc, d) => {
    const shots =
      doc && doc.evidence && Array.isArray(doc.evidence.shots)
        ? doc.evidence.shots
        : [];
    shots.forEach((shot, i) => {
      if (!shot || !shot.path) return;
      all.push({
        d,
        i,
        share: pictureScore(shot),
        view: {
          key: `view:${doc.targetId}/${i}`,
          path: shot.path,
          thumbPath: shot.thumbPath || null,
        },
      });
    });
  });
  const byShare = (a, b) => {
    if (a.share === null || b.share === null)
      return (a.share === null) - (b.share === null);
    return b.share - a.share;
  };
  return all
    .sort((a, b) => byShare(a, b) || a.d - b.d || a.i - b.i)
    .slice(0, VIEWS_PER_ROW)
    .map((a) => a.view);
}

/** A device's picture: its crop of the shot it was seen on, or null. */
function deviceView(doc, device) {
  const photo = evidencePhoto(doc, device);
  return photo
    ? { key: `device:${doc.targetId}/${device.id}`, ...photo }
    : null;
}

/**
 * A row's pictures in the homeowner's copy: each one uploaded with it, as its
 * link from `photos` (key → url); one that was not is left out, and no stored
 * path reaches a shared copy.
 */
function sharedViews(views, photos) {
  return (views || [])
    .map((v) => photos[v.key])
    .filter((url) => typeof url === 'string' && url)
    .map((url) => ({ url }));
}

/** Every row picture of a report, once each, room by room: what a share uploads. */
function reportViews(report) {
  const seen = new Map();
  for (const room of report.rooms || []) {
    for (const list of ROW_LISTS) {
      for (const row of room[list] || []) {
        for (const v of row.views || [])
          if (!seen.has(v.key)) seen.set(v.key, v);
      }
    }
  }
  return [...seen.values()];
}

module.exports = {
  VIEWS_PER_ROW,
  ROW_LISTS,
  reportViews,
  evidencePhoto,
  shotViews,
  deviceView,
  sharedViews,
};
